import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /** Identifies the latest option change so stale image decodes cannot move the gallery. */
  #selectionRequest = 0;

  /**
   * Serializes variant-driven slideshow changes so a busy transition cannot discard the latest selection.
   * @type {Promise<unknown>}
   */
  #selectionQueue = Promise.resolve();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#selectionRequest++;
  }

  /**
   * Handles a product select event by selecting an already-rendered image first,
   * then reconciling the gallery with the server response.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const request = ++this.#selectionRequest;
    const requestedMediaId = event.detail?.mediaId;

    if (requestedMediaId && this.presentation === 'carousel') {
      void this.#selectMediaWhenReady(requestedMediaId, request);
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html || request !== this.#selectionRequest || !this.isConnected) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const resolvedMediaId = newMediaGallery.querySelector('slideshow-slide[slide-id]')?.getAttribute('slide-id');

        // Variant responses commonly contain the same media in a different order. Keep
        // the live gallery so an already-decoded local selection is not replaced or flashed.
        if (this.presentation === 'carousel' && this.#hasSameMedia(newMediaGallery)) {
          if (resolvedMediaId && resolvedMediaId !== requestedMediaId) {
            void this.#selectMediaWhenReady(resolvedMediaId, request);
          }
          return;
        }

        void this.#replaceWhenReady(newMediaGallery, request);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Starts loading one gallery image in response to pointer or keyboard intent.
   *
   * @param {string} mediaId
   */
  preloadMedia(mediaId) {
    const image = this.#getMediaImage(mediaId);
    if (!image || image.complete) return;

    image.loading = 'eager';
  }

  /**
   * Keeps the current image visible until the requested image can be decoded,
   * then lets Horizon's slideshow perform its reduced-motion-aware transition.
   *
   * @param {string} mediaId
   * @param {number} request
   * @returns {Promise<boolean>}
   */
  async #selectMediaWhenReady(mediaId, request) {
    const image = this.#getMediaImage(mediaId);
    if (!image || !this.slideshow) return false;

    if (!image.complete || image.naturalWidth === 0) {
      image.loading = 'eager';
      image.fetchPriority = 'high';

      try {
        await image.decode();
      } catch {
        if (!image.complete) {
          await new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          });
        }
      }
    }

    if (request !== this.#selectionRequest || !this.isConnected || image.naturalWidth === 0) return false;

    const selection = this.#selectionQueue
      .then(async () => {
        const slideshow = this.slideshow;
        if (!slideshow || request !== this.#selectionRequest || !this.isConnected) return false;

        await slideshow.waitForIdle();
        if (request !== this.#selectionRequest || !this.isConnected) return false;

        await slideshow.select({ id: mediaId }, undefined, { animate: this.animateVariantImageChanges });
        await slideshow.waitForIdle();

        if (request !== this.#selectionRequest || !this.isConnected) return false;

        // A shared slideshow transition may have started immediately before this
        // queued selection. Verify the result and recover without another animation.
        if (this.#getActiveMediaId() !== mediaId) {
          await slideshow.select({ id: mediaId }, undefined, { animate: false });
          await slideshow.waitForIdle();
        }

        return this.#getActiveMediaId() === mediaId;
      })
      .catch((error) => {
        console.warn('[media-gallery] Unable to select variant media:', error);
        return false;
      });

    this.#selectionQueue = selection;
    return selection;
  }

  /**
   * @param {string} mediaId
   * @returns {HTMLImageElement | null}
   */
  #getMediaImage(mediaId) {
    const slide = this.querySelector(`slideshow-slide[slide-id="${CSS.escape(mediaId)}"]`);
    return slide?.querySelector('img.product-media__image') ?? null;
  }

  /**
   * @returns {string | null}
   */
  #getActiveMediaId() {
    const slideshow = this.slideshow;
    return slideshow?.slides?.[slideshow.current]?.getAttribute('slide-id') ?? null;
  }

  /**
   * @param {Element} gallery
   * @returns {boolean}
   */
  #hasSameMedia(gallery) {
    const currentMedia = new Set(
      Array.from(this.querySelectorAll('slideshow-slide[slide-id]'), (slide) => slide.getAttribute('slide-id'))
    );
    const newMedia = new Set(
      Array.from(gallery.querySelectorAll('slideshow-slide[slide-id]'), (slide) => slide.getAttribute('slide-id'))
    );

    if (currentMedia.size === 0 || currentMedia.size !== newMedia.size) return false;
    return Array.from(currentMedia).every((mediaId) => newMedia.has(mediaId));
  }

  /**
   * Warms the new lead image before a necessary full gallery replacement.
   *
   * @param {Element} newMediaGallery
   * @param {number} request
   */
  async #replaceWhenReady(newMediaGallery, request) {
    const sourceImage = newMediaGallery.querySelector('slideshow-slide[slide-id] img.product-media__image');

    if (sourceImage instanceof HTMLImageElement) {
      const preloadImage = new Image();
      preloadImage.decoding = 'async';
      preloadImage.fetchPriority = 'high';
      preloadImage.sizes = sourceImage.sizes;
      preloadImage.srcset = sourceImage.srcset;
      preloadImage.src = sourceImage.src;

      try {
        await preloadImage.decode();
      } catch {
        // Preserve Horizon's existing replacement fallback if an image cannot be decoded.
      }
    }

    if (request !== this.#selectionRequest || !this.isConnected) return;
    this.replaceWith(newMediaGallery);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * Whether variant-driven gallery selections should animate after the target image is ready.
   * Missing attributes preserve the smooth transition for older or externally rendered markup.
   */
  get animateVariantImageChanges() {
    return this.dataset.animateVariantImageChanges !== 'false';
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
